import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import StatusSemanticNote from '@/components/StatusSemanticNote';
import { API_BASE } from '@/lib/utils';
import {
  datetimeLocalToIso,
  formatInTimezone,
  timezoneLabel,
  useWorkspaceTimezone,
} from '@/lib/timezone';

const REGISTRATION_STATUSES = ['', 'Registered', 'Invited', 'Pending Approval', 'Cancelled', 'Paid'];

function ReportFilterField({ label, children, className = '' }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label className="text-overline block mb-2">{label}</label>
      {children}
    </div>
  );
}

export default function WorkspaceRegistrations() {
  const displayTimezone = useWorkspaceTimezone();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [registeredFrom, setRegisteredFrom] = useState('');
  const [registeredTo, setRegisteredTo] = useState('');
  const [registeredFromLocal, setRegisteredFromLocal] = useState('');
  const [registeredToLocal, setRegisteredToLocal] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/events`, { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json();
        setEvents(Array.isArray(payload.events) ? payload.events : []);
      } catch {
        setEvents([]);
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', offset: '0' });
      if (eventFilter) params.set('event_id', eventFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (registeredFrom) params.set('registered_from', registeredFrom);
      if (registeredTo) params.set('registered_to', registeredTo);
      const res = await fetch(`${API_BASE}/api/admin/reports/registrations?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load registrations');
      const payload = await res.json();
      setRows(Array.isArray(payload.rows) ? payload.rows : []);
      setTotal(Number(payload.total || 0));
    } catch (err) {
      toast.error('Unable to load registrations', { description: String(err?.message || 'unknown error') });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter, statusFilter, registeredFrom, registeredTo]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (eventFilter) params.set('event_id', eventFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (registeredFrom) params.set('registered_from', registeredFrom);
    if (registeredTo) params.set('registered_to', registeredTo);
    const url = `${API_BASE}/api/admin/reports/registrations/export.csv?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Export started');
  };

  const tzLabel = timezoneLabel(displayTimezone);
  const hasFilters = Boolean(eventFilter || statusFilter || registeredFromLocal || registeredToLocal);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto fade-up" data-testid="workspace-registrations">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-overline">Workspace</div>
          <h1 className="font-heading text-3xl mt-1">Registrations</h1>
          <p className="text-xs font-mono text-[var(--text-tertiary)] mt-1">
            {total.toLocaleString()} across all events · times in {tzLabel}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={load}>
            <RefreshCcw size={14} /> Refresh
          </button>
          <button type="button" className="btn-secondary" onClick={exportCsv}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="surface p-4 flex flex-wrap gap-4 items-end mb-6">
        <div>
          <label className="text-overline">Event</label>
          <select
            className="input-dark mt-2 min-w-[220px]"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
          >
            <option value="">All events</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title || ev.id}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-overline">Status</label>
          <select
            className="input-dark mt-2 min-w-[180px]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {REGISTRATION_STATUSES.filter(Boolean).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-overline">Registered from</label>
          <input
            type="datetime-local"
            className="input-dark mt-2 font-mono text-xs"
            value={registeredFromLocal}
            onChange={(e) => {
              const local = e.target.value;
              setRegisteredFromLocal(local);
              setRegisteredFrom(local ? datetimeLocalToIso(local, displayTimezone) : '');
            }}
          />
        </div>
        <div>
          <label className="text-overline">Registered to</label>
          <input
            type="datetime-local"
            className="input-dark mt-2 font-mono text-xs"
            value={registeredToLocal}
            onChange={(e) => {
              const local = e.target.value;
              setRegisteredToLocal(local);
              setRegisteredTo(local ? datetimeLocalToIso(local, displayTimezone) : '');
            }}
          />
        </div>
        {(registeredFromLocal || registeredToLocal || statusFilter || eventFilter) ? (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => {
              setEventFilter('');
              setStatusFilter('');
              setRegisteredFrom('');
              setRegisteredTo('');
              setRegisteredFromLocal('');
              setRegisteredToLocal('');
            }}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {loading ? (
        <StatusSemanticNote state="loading" message="Loading registration report…" />
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
              <tr>
                <th className="p-4 text-left">Event</th>
                <th className="p-4 text-left">Name</th>
                <th className="p-4 text-left">Email</th>
                <th className="p-4 text-left">Company</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Ticket</th>
                <th className="p-4 text-left">Registered</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.eventId}-${r.id}`} className="border-b border-[var(--border-default)]">
                  <td className="p-4">
                    {r.eventId ? (
                      <Link
                        to={`/admin/events/${r.eventId}/attendees`}
                        className="text-[var(--primary)] hover:underline"
                      >
                        {r.eventTitle || r.eventId}
                      </Link>
                    ) : (
                      r.eventTitle || '—'
                    )}
                  </td>
                  <td className="p-4">{r.name}</td>
                  <td className="p-4 font-mono text-xs">{r.email}</td>
                  <td className="p-4 text-[var(--text-secondary)]">{r.company || '—'}</td>
                  <td className="p-4 text-xs">{r.status}</td>
                  <td className="p-4 text-xs text-[var(--text-tertiary)]">{r.ticket || '—'}</td>
                  <td className="p-4 font-mono text-xs whitespace-nowrap" title={r.registeredAt || ''}>
                    {formatInTimezone(r.registeredAt, displayTimezone, { second: '2-digit' })}
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td className="p-8 text-[var(--text-secondary)]" colSpan={7}>
                    {hasFilters ? (
                      'No registrations match the current filters.'
                    ) : (
                      <span>
                        No registrations yet. They appear here when attendees register for your events.{' '}
                        <Link to="/admin/events" className="text-[var(--primary)] hover:underline">
                          View events
                        </Link>
                      </span>
                    )}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {total > rows.length ? (
            <div className="p-4 text-xs font-mono text-[var(--text-tertiary)] border-t border-[var(--border-default)]">
              Showing {rows.length} of {total.toLocaleString()}. Narrow filters or export CSV for the full list.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
